import { useCallback, useState } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import klydoApiClient, { PopulatedKlydo } from '../../../../api/klydo';
import { useNavigate } from 'react-router-dom';
import { generateObjectId } from '../../../../utils';
import { KlydoFilterTab, getApiFiltersForTab } from '../types/filterTypes';

const PAGE_SIZE = 20;

const useKlydosList = () => {
  const [searchText, setSearchText] = useState('');
  const [activeFilter, setActiveFilter] = useState<KlydoFilterTab>(
    KlydoFilterTab.All,
  );

  const navigate = useNavigate();

  const queryClient = useQueryClient();
  const handleTextChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchText(event.target.value);
    },
    [],
  );

  const { data, fetchNextPage, isLoading, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery<PopulatedKlydo[], Error>({
      queryKey: ['klydosList', searchText, activeFilter],
      queryFn: async ({ pageParam }) => {
        const { skip = 0 } = pageParam as { skip?: number };
        const searchOptions = {
          limit: PAGE_SIZE,
          skip,
          sort: { createdAt: -1 },
          filters: getApiFiltersForTab(activeFilter),
        };
        const res = await klydoApiClient.search(searchText, searchOptions);

        res.forEach((klydo) => {
          queryClient.setQueryData(['klydo', klydo.id], klydo);
        });

        return res;
      },
      getNextPageParam: (lastPage, allPages) => {
        const currentLength = allPages.flat().length;
        return currentLength < PAGE_SIZE ? undefined : { skip: currentLength };
      },
      initialPageParam: { skip: 0 },
    });

  const handleCreateClick = () => {
    const createdObjectId = generateObjectId();
    navigate(`/klydo/${createdObjectId}`);
  };

  const handleFilterChange = useCallback((filter: KlydoFilterTab) => {
    setActiveFilter(filter);
  }, []);

  const flatData = data?.pages.flat();

  return {
    data: flatData,
    isLoading,
    searchText,
    isFetchingNextPage,
    hasNextPage,
    handleTextChange,
    fetchNextPage,
    handleCreateClick,
    activeFilter,
    handleFilterChange,
  };
};

export default useKlydosList;
